import React, { useEffect, useState, useRef } from 'react';
import { UsersIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon, SearchIcon, EyeIcon, PauseIcon, PlayIcon, TrashIcon, MoreVerticalIcon } from 'lucide-react';
import { InviteClientModal } from './InviteClientModal';
import { useNavigate } from 'react-router-dom';
import { useClient } from '../../contexts/ClientContext';
import { usePreventScroll } from '../../hooks/usePreventScroll';
import { useAuth } from '../../contexts/AuthContext'; // Add this import
interface Client {
  id: string;
  name: string;
  domain: string; // Changed from email to domain to match Client type
  spacesUsed: number;
  spacesLimit: number;
  chatbotsUsed: number;
  chatbotsLimit: number;
  status: 'active' | 'pending' | 'suspended';
  lastActive: string;
  estimatedBilling?: string; // Add new field
}
interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel: string;
  type: 'pause' | 'delete';
  clientName: string; // Add clientName prop
}
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  type,
  clientName
}: ConfirmationModalProps) => {
  const [isConfirmChecked, setIsConfirmChecked] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  // Add scroll prevention
  usePreventScroll(isOpen);
  if (!isOpen) return null;
  const isConfirmEnabled = isConfirmChecked && confirmText === clientName;
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()} data-id="element-48">
      <div className="bg-surface-light border border-surface-border rounded-none w-full max-w-md animate-in fade-in-50 slide-in-from-bottom-4 sm:slide-in-from-bottom-0" onClick={e => e.stopPropagation()} data-id="element-49">
        <div className="px-6 py-4 border-b border-surface-border" data-id="element-50">
          <h2 className="text-lg font-medium text-white font-mono" data-id="element-51">{title}</h2>
        </div>
        <div className="p-6" data-id="element-52">
          <div className="space-y-4" data-id="element-53">
            <p className="text-gray-400" data-id="element-54">{message}</p>
            {/* Warning Box */}
            <div className={`p-4 ${type === 'delete' ? 'bg-status-error/10 border-status-error/30' : 'bg-surface border-surface-border'} border rounded-none`} data-id="element-55">
              <p className="text-sm text-gray-400" data-id="element-56">
                This action {type === 'delete' ? 'cannot' : 'can'} be undone.
                Please type{' '}
                <span className="font-mono text-white" data-id="element-57">{clientName}</span> to
                confirm:
              </p>
              <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="Type the organization name here" className="mt-3 w-full px-3 py-2 bg-surface border border-surface-border rounded-none 
                         text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                         focus:ring-accent focus:border-transparent" data-id="element-58" />
            </div>
            {/* Confirmation Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer" data-id="element-59">
              <input type="checkbox" checked={isConfirmChecked} onChange={e => setIsConfirmChecked(e.target.checked)} className="mt-1" data-id="element-60" />
              <span className="text-sm text-gray-400" data-id="element-61">
                I understand that I am about to{' '}
                {type === 'delete' ? 'permanently delete' : 'pause'} the
                organization "{clientName}"
                {type === 'delete' ? ' and all of its data will be permanently lost' : ' and all users will lose access to their workspaces'}
              </span>
            </label>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-surface-border flex justify-end gap-3" data-id="element-62">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors" data-id="element-63">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={!isConfirmEnabled} className={`px-4 py-2 text-sm rounded-none flex items-center gap-2 transition-colors
              ${type === 'delete' ? 'bg-status-error hover:bg-status-error/90 text-white disabled:bg-status-error/50' : 'bg-accent hover:bg-accent-light text-white disabled:bg-accent/50'} 
              disabled:cursor-not-allowed`} data-id="element-64">
            {type === 'delete' ? <TrashIcon size={16} data-id="element-65" /> : <PauseIcon size={16} data-id="element-66" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>;
};
console.log('ClientList component loaded - v2'); // We'll remove this after testing
export const ClientList = () => {
  const navigate = useNavigate();
  const {
    setCurrentClient,
    setIsImpersonating
  } = useClient();
  const {
    logout
  } = useAuth(); // Add this line to get logout function
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [sortField, setSortField] = useState<keyof Client>('lastActive');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Update the mock client data
  const clients: Client[] = [{
    id: '1',
    name: 'Near Bridge',
    domain: 'admin@nearbridge.ai',
    spacesUsed: 4,
    spacesLimit: 5,
    chatbotsUsed: 12,
    chatbotsLimit: 15,
    status: 'active',
    lastActive: '2025-04-28T10:30:00Z',
    estimatedBilling: '$120.00'
  }, {
    id: '3',
    name: 'AgileBrain',
    domain: 'team@agilebrain.io',
    spacesUsed: 5,
    spacesLimit: 5,
    chatbotsUsed: 18,
    chatbotsLimit: 20,
    status: 'active',
    lastActive: '2025-04-28T11:15:00Z',
    estimatedBilling: '$180.00'
  }, {
    id: '2',
    name: 'Patrick Hourihane',
    domain: 'patrick@hourihane.com',
    spacesUsed: 2,
    spacesLimit: 3,
    chatbotsUsed: 6,
    chatbotsLimit: 10,
    status: 'active',
    lastActive: '2025-04-28T09:45:00Z',
    estimatedBilling: '$48.00'
  }, {
    id: '6',
    name: 'Sprinklenet Website',
    domain: 'web@sprinklenet.com',
    spacesUsed: 1,
    spacesLimit: 1,
    chatbotsUsed: 2,
    chatbotsLimit: 3,
    status: 'active',
    lastActive: '2025-04-28T12:00:00Z',
    estimatedBilling: '$20.00'
  }, {
    id: '5',
    name: 'DCAA Demo',
    domain: 'demo@dcaa.mil',
    spacesUsed: 2,
    spacesLimit: 3,
    chatbotsUsed: 4,
    chatbotsLimit: 8,
    status: 'active',
    lastActive: '2025-04-28T08:45:00Z',
    estimatedBilling: '$40.00'
  }, {
    id: '4',
    name: 'ICE Demo',
    domain: 'demo@ice.gov',
    spacesUsed: 1,
    spacesLimit: 2,
    chatbotsUsed: 3,
    chatbotsLimit: 5,
    status: 'pending',
    lastActive: '2025-04-27T15:30:00Z',
    estimatedBilling: '$30.00'
  }, {
    id: '7',
    name: 'Global Research Ltd',
    domain: 'research@global.org',
    spacesUsed: 5,
    spacesLimit: 5,
    chatbotsUsed: 12,
    chatbotsLimit: 20,
    status: 'active',
    lastActive: '2025-04-28T09:15:00Z',
    estimatedBilling: '$120.00'
  }];
  const handleSort = (field: keyof Client) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const filteredClients = clients.filter(client => client.name.toLowerCase().includes(searchQuery.toLowerCase()) || client.domain.toLowerCase().includes(searchQuery.toLowerCase())).sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    const modifier = sortDirection === 'asc' ? 1 : -1;
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue) * modifier;
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return (aValue - bValue) * modifier;
    }
    return 0;
  });
  const getStatusColor = (status: Client['status']) => {
    switch (status) {
      case 'active':
        return 'bg-status-success/10 border-status-success/30 text-status-success';
      case 'pending':
        return 'bg-status-warning/10 border-status-warning/30 text-status-warning';
      case 'suspended':
        return 'bg-status-error/10 border-status-error/30 text-status-error';
    }
  };
  const handleImpersonateClient = (client: Client) => {
    if (!client) return; // Add guard clause
    setCurrentClient({
      id: client.id,
      name: client.name,
      domain: client.domain // Fix: Change from email to domain
    });
    setIsImpersonating(true);
    navigate('/dashboard');
  };
  const handleDeleteClient = (client: Client, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedClient(client);
    setShowDeleteModal(true);
  };
  const handlePauseClient = (client: Client, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedClient(client);
    setShowPauseModal(true);
  };
  const confirmDelete = () => {
    if (selectedClient) {
      console.log('Deleting client:', selectedClient.id);
      // Here you would typically make an API call to delete the client
      // For now, we'll just close the modal and clear the selection
      setShowDeleteModal(false);
      setSelectedClient(null);
      setOpenMenuId(null);
    }
  };
  const confirmPause = () => {
    if (selectedClient) {
      console.log('Pausing client:', selectedClient.id);
      // Here you would typically make an API call to pause/resume the client
      // For now, we'll just close the modal and clear the selection
      setShowPauseModal(false);
      setSelectedClient(null);
      setOpenMenuId(null);
    }
  };
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const renderMobileActions = (client: Client) => <div className="flex items-center justify-between" data-id="element-67">
      <button onClick={() => handleImpersonateClient(client)} className="flex items-center gap-1.5 text-accent hover:text-accent-light transition-colors" data-id="element-68">
        <EyeIcon size={14} data-id="element-69" />
        <span data-id="element-70">Access Workspace</span>
      </button>
      <div className="flex items-center gap-3" data-id="element-71">
        <button onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedClient(client);
        setShowPauseModal(true);
      }} className="text-gray-400 hover:text-white flex items-center gap-1 transition-colors" data-id="element-72">
          {client.status === 'suspended' ? <>
              <PlayIcon size={14} data-id="element-73" />
              <span data-id="element-74">Resume</span>
            </> : <>
              <PauseIcon size={14} data-id="element-75" />
              <span data-id="element-76">Pause</span>
            </>}
        </button>
        <button onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedClient(client);
        setShowDeleteModal(true);
      }} className="text-status-error hover:text-status-error/80 flex items-center gap-1 transition-colors" data-id="element-77">
          <TrashIcon size={14} data-id="element-78" />
          <span data-id="element-79">Delete</span>
        </button>
      </div>
    </div>;
  const renderDesktopActions = (client: Client) => <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-80">
      <div className="flex items-center justify-end gap-2" data-id="element-81">
        <button onClick={() => handleImpersonateClient(client)} className="bg-accent text-white px-6 py-2 rounded-none hover:bg-accent-light transition-colors flex items-center gap-2" data-id="element-82">
          <EyeIcon size={16} data-id="element-83" />
          <span data-id="element-84">Access Workspace</span>
        </button>
        <div className="relative" data-id="element-85">
          <button onClick={() => setOpenMenuId(openMenuId === client.id ? null : client.id)} className="p-2 text-gray-400 hover:text-white hover:bg-surface rounded-none transition-colors" aria-label="More actions" data-id="element-86">
            <MoreVerticalIcon size={16} data-id="element-87" />
          </button>
          {openMenuId === client.id && <div className="absolute right-0 top-full mt-1 w-48 bg-surface-light border border-surface-border rounded-none shadow-lg z-[100]" data-id="element-88">
              <div className="py-1" data-id="element-89">
                <button onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedClient(client);
              setShowPauseModal(true);
              setOpenMenuId(null);
            }} className="w-full px-4 py-2 text-sm text-left text-gray-400 hover:text-white hover:bg-surface flex items-center gap-2" data-id="element-90">
                  {client.status === 'suspended' ? <>
                      <PlayIcon size={16} data-id="element-91" />
                      <span data-id="element-92">Resume Access</span>
                    </> : <>
                      <PauseIcon size={16} data-id="element-93" />
                      <span data-id="element-94">Pause Access</span>
                    </>}
                </button>
                <div className="border-t border-surface-border my-1" data-id="element-95" />
                <button onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedClient(client);
              setShowDeleteModal(true);
              setOpenMenuId(null);
            }} className="w-full px-4 py-2 text-sm text-left text-status-error hover:bg-surface flex items-center gap-2" data-id="element-96">
                  <TrashIcon size={16} data-id="element-97" />
                  <span data-id="element-98">Delete Organization</span>
                </button>
              </div>
            </div>}
        </div>
      </div>
    </td>;
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!event.target) return;
      const target = event.target as Node;
      const isMenuButton = target.closest('[aria-label="More actions"]');
      if (!isMenuButton) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openMenuId]);
  return <div className="space-y-6" data-id="element-99">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-100">
        <div data-id="element-101">
          <h1 className="text-xl sm:text-2xl font-semibold text-white" data-id="element-102">
            Clients
          </h1>
          <p className="mt-1 text-sm text-gray-400" data-id="element-103">
            Manage client organizations and their access
          </p>
        </div>
        <button onClick={() => setShowInviteModal(true)} className="flex items-center justify-center gap-2 px-4 py-2 bg-accent hover:bg-accent-light 
                   text-white rounded-none transition-colors font-mono text-sm uppercase tracking-wide 
                   w-full sm:w-auto" data-id="element-104">
          <PlusIcon size={18} data-id="element-105" />
          <span data-id="element-106">Invite New Client</span>
        </button>
      </div>
      {/* Search Bar */}
      <div className="bg-surface-light border border-surface-border rounded-none overflow-hidden" data-id="element-107">
        <div className="p-3 sm:p-4 border-b border-surface-border" data-id="element-108">
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-109">
            <div className="flex-1 relative" data-id="element-110">
              <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-111" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search clients..." className="w-full pl-10 pr-4 py-2 bg-surface border border-surface-border rounded-none 
                         text-sm text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 
                         focus:ring-accent focus:border-transparent transition-all font-mono" data-id="element-112" />
            </div>
          </div>
        </div>
        {/* Mobile List View */}
        <div className="block sm:hidden divide-y divide-surface-border" data-id="element-113">
          {filteredClients.map(client => <div key={client.id} className="p-4 hover:bg-surface transition-colors animate-in fade-in-50" data-id="element-114">
              <div className="flex justify-between items-start mb-3" data-id="element-115">
                <div data-id="element-116">
                  <div className="text-sm font-medium text-white font-mono" data-id="element-117">
                    {client.name}
                  </div>
                  <div className="text-sm text-gray-400" data-id="element-118">{client.domain}</div>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-none text-xs font-medium capitalize 
                           ${getStatusColor(client.status)}`} data-id="element-119">
                  {client.status}
                </span>
              </div>
              <div className="space-y-3 mb-4" data-id="element-120">
                <div data-id="element-121">
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-1" data-id="element-122">
                    <span data-id="element-123">Knowledge Spaces</span>
                    <span className="font-mono" data-id="element-124">
                      {client.spacesUsed}/{client.spacesLimit}
                    </span>
                  </div>
                  <div className="h-1.5 bg-surface-border rounded-none overflow-hidden" data-id="element-125">
                    <div className="h-full bg-accent rounded-none transition-all duration-300" style={{
                  width: `${client.spacesUsed / client.spacesLimit * 100}%`
                }} data-id="element-126" />
                  </div>
                </div>
                <div data-id="element-127">
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-1" data-id="element-128">
                    <span data-id="element-129">Chatbots</span>
                    <span className="font-mono" data-id="element-130">
                      {client.chatbotsUsed}/{client.chatbotsLimit}
                    </span>
                  </div>
                  <div className="h-1.5 bg-surface-border rounded-none overflow-hidden" data-id="element-131">
                    <div className="h-full bg-accent rounded-none transition-all duration-300" style={{
                  width: `${client.chatbotsUsed / client.chatbotsLimit * 100}%`
                }} data-id="element-132" />
                  </div>
                </div>
              </div>
              {renderMobileActions(client)}
            </div>)}
        </div>
        {/* Desktop Table View */}
        <div className="hidden sm:block overflow-x-auto" data-id="element-133">
          <table className="w-full" data-id="element-134">
            <thead data-id="element-135">
              <tr className="bg-surface" data-id="element-136">
                <th className="px-6 py-4 text-left" data-id="element-137">
                  <button onClick={() => handleSort('name')} className="flex items-center gap-2 text-xs font-medium text-gray-400 font-mono uppercase tracking-wider hover:text-gray-300 transition-colors group" data-id="element-138">
                    Organization
                    {sortField === 'name' && (sortDirection === 'asc' ? <ArrowUpIcon size={14} className="text-accent" data-id="element-139" /> : <ArrowDownIcon size={14} className="text-accent" data-id="element-140" />)}
                  </button>
                </th>
                <th className="px-6 py-4 text-left" data-id="element-141">
                  <span className="text-xs font-medium text-gray-400 font-mono uppercase tracking-wider" data-id="element-142">
                    Status
                  </span>
                </th>
                <th className="px-6 py-4 text-left" data-id="element-143">
                  <span className="text-xs font-medium text-gray-400 font-mono uppercase tracking-wider" data-id="element-144">
                    Knowledge Spaces
                  </span>
                </th>
                <th className="px-6 py-4 text-left" data-id="element-145">
                  <span className="text-xs font-medium text-gray-400 font-mono uppercase tracking-wider" data-id="element-146">
                    Chatbots
                  </span>
                </th>
                <th className="px-6 py-4 text-left" data-id="element-147">
                  <button onClick={() => handleSort('lastActive')} className="flex items-center gap-2 text-xs font-medium text-gray-400 font-mono uppercase tracking-wider hover:text-gray-300 transition-colors group" data-id="element-148">
                    Last Active
                    {sortField === 'lastActive' && (sortDirection === 'asc' ? <ArrowUpIcon size={14} className="text-accent" data-id="element-149" /> : <ArrowDownIcon size={14} className="text-accent" data-id="element-150" />)}
                  </button>
                </th>
                <th className="px-6 py-4 text-left" data-id="element-151">
                  <span className="text-xs font-medium text-gray-400 font-mono uppercase tracking-wider" data-id="element-152">
                    Monthly Billing
                  </span>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-153">
                  <span className="text-xs font-medium text-gray-400 font-mono uppercase tracking-wider" data-id="element-154">
                    Actions
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border" data-id="element-155">
              {filteredClients.map(client => <tr key={client.id} className="hover:bg-surface transition-colors" data-id="element-156">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-157">
                    <div data-id="element-158">
                      <div className="text-sm font-medium text-gray-100" data-id="element-159">
                        {client.name}
                      </div>
                      <div className="text-sm text-gray-400" data-id="element-160">
                        {client.domain}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-161">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(client.status)}`} data-id="element-162">
                      {client.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-163">
                    <div className="flex items-center gap-2" data-id="element-164">
                      <div className="flex-1 h-2 bg-surface-border rounded-none overflow-hidden" data-id="element-165">
                        <div className="h-full bg-accent rounded-none transition-all duration-300" style={{
                      width: `${client.spacesUsed / client.spacesLimit * 100}%`
                    }} data-id="element-166" />
                      </div>
                      <span className="text-sm text-gray-400 font-mono" data-id="element-167">
                        {client.spacesUsed}/{client.spacesLimit}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-168">
                    <div className="flex items-center gap-2" data-id="element-169">
                      <div className="flex-1 h-2 bg-surface-border rounded-none overflow-hidden" data-id="element-170">
                        <div className="h-full bg-accent rounded-none transition-all duration-300" style={{
                      width: `${client.chatbotsUsed / client.chatbotsLimit * 100}%`
                    }} data-id="element-171" />
                      </div>
                      <span className="text-sm text-gray-400 font-mono" data-id="element-172">
                        {client.chatbotsUsed}/{client.chatbotsLimit}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-173">
                    {new Date(client.lastActive).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium" data-id="element-174">
                    <span className="text-accent-light font-mono" data-id="element-175">
                      {client.estimatedBilling}
                    </span>
                  </td>
                  {renderDesktopActions(client)}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
      <InviteClientModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} data-id="element-176" />
      <ConfirmationModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} onConfirm={confirmDelete} title="Delete Client Organization" message={`Are you sure you want to delete ${selectedClient?.name}? This action cannot be undone and will permanently remove all associated data.`} confirmLabel="Delete Organization" type="delete" clientName={selectedClient?.name || ''} data-id="element-177" />
      <ConfirmationModal isOpen={showPauseModal} onClose={() => setShowPauseModal(false)} onConfirm={confirmPause} title={selectedClient?.status === 'suspended' ? 'Resume Client Access' : 'Pause Client Access'} message={selectedClient?.status === 'suspended' ? `Are you sure you want to resume access for ${selectedClient?.name}? This will restore their ability to use all features.` : `Are you sure you want to pause access for ${selectedClient?.name}? This will temporarily prevent them from accessing the platform.`} confirmLabel={selectedClient?.status === 'suspended' ? 'Resume Access' : 'Pause Access'} type="pause" clientName={selectedClient?.name || ''} data-id="element-178" />
    </div>;
};